<template>
    <div id="q-app">
        <router-view />
        <q-expansion-item
            id="logConsole"
            v-if="$q.platform.is.mobile"
            header-class="text-secondary logs-expansion"
            header-style="width:100%;"
            expand-icon-class="text-white"
            dense
        >
            <template v-slot:header>
                <q-item-section>
                    <q-avatar font-size="11px">
                        <q-icon
                            name="fal fa-terminal"
                            color="secondary"
                            size="xs"
                        ></q-icon>
                    </q-avatar>
                </q-item-section>
                <q-item-section>{{ $t('Debug') }}</q-item-section>
            </template>
            <q-card bordered>
                <q-card-section>
                    <div id="error-log" ref="error-log" class="error-log"></div>
                </q-card-section>
            </q-card>
        </q-expansion-item>
    </div>
</template>

<script>
import ConsoleLogsMixin from '@mixins/ConsoleLogs.vue';
import { mapState, mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'App',
    mixins: [ConsoleLogsMixin],
    async created() {
        this.syncUserLogin();
        if (this.isCapacitor) {
            await this.$pushNotificationManager.initialize();
        }
    },
    watch: {
        /**
         * Show notification popup whenever the notification contents in store changes.
         */
        notification() {
            if (this.notification.message) {
                if (this.notification.type === 'success') {
                    this.$notifySuccess(this.notification.message);
                } else {
                    this.$notifyError(this.notification.message);
                }
            }
        },
        getUser: {
            immediate: true,
            async handler() {
                // Capacitor-specific build - Uses new push notification system via Notification Hub
                if (this.isCapacitor) {
                    await this.managePushNotifications();
                }
            }
        }
    },
    computed: {
        ...mapState(useSharedStore, ['notification']),
        ...mapState(useAuthStore, {
            getUser: 'user'
        }),
        /**
         * Check if Capacitor build
         * @returns boolean
         */
        isCapacitor() {
            return this.$q.platform.is.capacitor;
        }
    },
    methods: {
        ...mapActions(useAuthStore, [
            'hasValidRefreshTokenAction',
            'setJWTAction',
            'isAuthenticatedAction',
            'updateUserAction'
        ]),
        /**
         * Register / unregister the device for push notifications when user is logged in/out.
         * Only applicable for Capacitor platform.
         */
        async managePushNotifications() {
            const isAuthenticated = await this.isAuthenticatedAction();
            if (isAuthenticated) {
                await this.$pushNotificationManager.registerDevice();
            } else {
                await this.$pushNotificationManager.unregisterDevice();
            }
        },
        /**
         * Sync login between tabs/windows to ensure that the same user is logged in.
         *
         * When multiple tabs or windows are open and the user logs in, it is only the
         * current tab that will update it's VueX states (VueX does not listen to what
         * is happening in other tabs/windows). So a user can be authenticated in one
         * tab/window but not in others. This can lead to having different users logged
         * in sharing the same localstorage data.
         *
         * We add this event listener to prevent this from happening. This event listener
         * updates the VueX states across tabs/windows whenever localstorage changes.
         */
        syncUserLogin() {
            // update VueX states
            window.addEventListener('storage', async event => {
                // update user in vuex store on localstorage user change
                const user = JSON.parse(localStorage.getItem('user'));
                if (user && event.key === 'user') {
                    this.updateUserAction(user);
                }

                // update token in vuex store on localstorage token change
                const token = JSON.parse(localStorage.getItem('token'));
                if (token && event.key === 'token') {
                    // load token into store
                    this.setJWTAction(token);
                }

                const isAuthenticated = await this.isAuthenticatedAction();

                // navigate user to facility list page if user was just authenticated
                if (
                    isAuthenticated &&
                    this.$route.name !== 'facility_list' &&
                    this.$router.currentRoute.name === 'login'
                ) {
                    this.$router.push({ name: 'facility_list' });
                    // if the user is not authenticated and the current route
                    // is not public, navigate to login page
                } else if (!isAuthenticated && !this.$route.meta.isPublic) {
                    // navigate user to login page if not authenticated
                    this.$router.push({ name: 'login' });
                }
            });
        }
    }
};
</script>

<style scoped>
.error-log {
    max-height: 100%;
    overflow-y: scroll;
    font-family: 'Courier', 'Courier New', monospace;
    font-size: 0.7rem;
}

.logs-expansion {
    display: block;
}
</style>
